import {
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges
} from '@angular/core';
import { ResponseTypeOptions } from '../../../enums/response-type-enum';
import {
  ProjectValidationCriteria,
  ProjectValidationCriteriaResponse
} from '../../../models/projectvalidationcriteria';
import { UserLogin } from '../../../../Common/models/user.login';
import { StorageService } from '../../../../Common/services/storage.service';
import { VerificationService } from '../../../../verification/services/verification.service';
import { ProjectConstants } from '../../../constants/component-messages';
import { ConfirmationService } from 'primeng/api';
import { ToastrService } from 'ngx-toastr';
import { UnsavedChangesService } from '../../../../unsaved-changes.service';
import { MasterService } from '../../../../administration/services/master.service';
import { CriteriaDto } from '../../../models/criteria';
import { CriteriaTypeOptions } from '../../../enums/criteria-type-enum';

@Component({
  selector: 'app-sre-validation-criteria',
  templateUrl: './sre-validation-criteria.component.html',
  styleUrl: './sre-validation-criteria.component.css'
})
export class SreValidationCriteriaComponent {
  @Input() disableAfterReview = false;
  @Input() selectedValidationCriteriaRows?: ProjectValidationCriteria[];
  @Output() updateValidationCriteriaRows = new EventEmitter<ProjectValidationCriteria[]>();

  validationCriterias: ProjectValidationCriteria[] = [];

  parentCol = "text-center align-middle tdHeaderBg";
  parentColAutoComplete = "tdHeaderBg position-relative";
  childCol = "text-center align-middle";
  childColAutoComplete = "align-middle";

  uInfo: UserLogin | null | undefined;

  // State
  disableAgencySection: any;
  isStoreLevel = false;
  isStoreAndProductLevel = false;

  // Criteria
  criteriaTypes = Object.values(CriteriaTypeOptions);
  criteriaList: CriteriaDto[] = [];
  responseDropdownValues: string[] = [];

  selectedCriteriaType: string | null = null;
  selectedCriteria: CriteriaDto | null = null;
  customCriteriaText = '';
  selectedResponseType: string | null = null;
  customResponses: string[] = [''];
  isPartialCriteriaText = false;
  partialCriteriaText = '';
  splitPartialCriteriaText: { text: string, isInput: boolean, placeholder: string }[] = [];
  partialInputValue: string[] = [];

  selectedAction = 'ADD';
  selectedProjectValidationCriteriaForEdit!: ProjectValidationCriteria;
  editingCriteriaIndex: number | undefined;

  constructor(
    private storageService: StorageService,
    private verificaionService: VerificationService,
    private confirmationService: ConfirmationService,
    private toastr: ToastrService,
    private unsavedChangesService: UnsavedChangesService,
    private masterService: MasterService
  ) { }

  ngOnInit() {
    this.uInfo = this.storageService.getItem<UserLogin>("UserInfo");
    this.loadCriteriaAndResponses();

    if (this.selectedValidationCriteriaRows) {
      this.selectedValidationCriteriaRows.forEach(item => {
        item.responsesText = this.getResponseText(item.lstProjectValidationCriteriaResponse);
        item.isChanged = false;
      });

      this.validationCriterias = [...this.selectedValidationCriteriaRows];
    }
  }

  ngOnChanges(changes: SimpleChanges) { }

  loadCriteriaAndResponses(): void {
    this.masterService.getCriteriaAndResponses().subscribe(data => {
      this.criteriaList = data;
    });
  }

  onCriteriaTypeChange(type: string): void {
    this.selectedCriteriaType = type;
    this.selectedCriteria = null;
    this.customCriteriaText = '';
    this.customResponses = [];
    this.partialCriteriaText = '';

    if (type === 'Custom') {
      this.responseDropdownValues = Object.values(ResponseTypeOptions);
      this.selectedResponseType = null;
    } else if (type === CriteriaTypeOptions.Standard) {
      this.selectedResponseType = 'Dropdown';
      this.isPartialCriteriaText = false;
    }
  }

  onCriteriaDropdownUpdate(event: any): void {
    this.selectedCriteria = event;

    if(this.selectedCriteria?.isMultiSelect){
      this.selectedResponseType = 'MultiSelect';
    }
    else{
      this.selectedResponseType = 'Dropdown';
    }

    this.isPartialCriteriaText = !!this.selectedCriteria?.isPartial;

    if (this.isPartialCriteriaText) {
      this.buildSplitPartialText(this.selectedCriteria?.criteriaText || '');
    }

    this.customResponses = [];
    if (this.selectedCriteria?.responses.length === 0) {
      this.addResponse();
    }
  }

  addResponse(): void {
    this.customResponses.push('');
  }

  removeResponse(index: number): void {
    if (this.customResponses.length > 1) {
      this.customResponses.splice(index, 1);
    }
  }

  addValidationCriteria(): void {
    if (this.disableAgencySection || this.disableAfterReview) return;

    if (!this.validateCriteria()) return;

    this.partialCriteriaText = this.getReconstructedPartialCriteriaText();
    const selectedCriteriaText = this.getSelectedCriteriaText();
    const selectedCriteriaId = this.selectedCriteria?.criteriaID || 0;
    const responses = this.buildProjectValidationCriteriaResponses();

    const updatedCriteria: ProjectValidationCriteria = {
      projectValidationCriteriaID: this.selectedProjectValidationCriteriaForEdit?.projectValidationCriteriaID ?? 0,
      projectID: 0,
      storeLevel: undefined,
      productLevel: undefined,
      criteriaText: selectedCriteriaText,
      criteriaID: selectedCriteriaId,
      responseType: this.selectedResponseType,
      responsesText: this.getResponseText(responses),
      isApproved: true,
      createdBy: this.uInfo?.userID ?? 0,
      createdDate: new Date(),
      lstProjectValidationCriteriaResponse: responses,
      isChanged: true
    };

    if (this.selectedAction === 'EDIT') {
      this.updateExistingCriteria(updatedCriteria);
    } else {
      this.addNewCriteria(updatedCriteria);
    }

    this.unsavedChangesService.setUnsavedChanges(true);
    this.emitUpdatedRows();
    this.resetCriteria();
  }

  private updateExistingCriteria(criteria: ProjectValidationCriteria): void {
    let isDuplicate = false;

    this.validationCriterias.forEach((c, i) => {
      const isSameRecord = this.selectedProjectValidationCriteriaForEdit?.projectValidationCriteriaID
        ? c.projectValidationCriteriaID === this.selectedProjectValidationCriteriaForEdit.projectValidationCriteriaID
        : i === this.editingCriteriaIndex;

      if (isSameRecord) return; // Skip current editing row

      if (criteria.responseType === 'Dropdown') {
        if(this.isPartialCriteriaText){
          if (c.criteriaText === criteria.criteriaText) {
            isDuplicate = true;
          }
        }
        else{
          if (c.criteriaID === criteria.criteriaID) {
            isDuplicate = true;
          }
        }
      } else {
        if (
          c.criteriaText === criteria.criteriaText &&
          c.responseType === criteria.responseType
        ) {
          isDuplicate = true;
        }
      }
    });

    if (isDuplicate) {
      this.toastr.error(ProjectConstants.VALIDATION_CRITERIA_DUPLICATE_SELECTION);
      return;
    }

    if (this.editingCriteriaIndex !== undefined && this.editingCriteriaIndex > -1) {
      const updatedList = [...this.validationCriterias];
      updatedList[this.editingCriteriaIndex] = criteria;
      this.validationCriterias = updatedList;
    }

    this.selectedProjectValidationCriteriaForEdit = undefined!;
    this.editingCriteriaIndex = undefined!;
    this.selectedAction = 'ADD';
  }

  private addNewCriteria(criteria: ProjectValidationCriteria): void {
    let isDuplicate = false;

    if (criteria.responseType === 'Dropdown') {
      if(this.isPartialCriteriaText){
        isDuplicate = this.validationCriterias.some(
          c => c.criteriaID === criteria.criteriaID && c.criteriaText === criteria.criteriaText
        );
      }
      else{
        isDuplicate = this.validationCriterias.some(
          c => c.criteriaID === criteria.criteriaID
        );
      }
    } else {
      isDuplicate = this.validationCriterias.some(
        c => c.criteriaText === criteria.criteriaText &&
          c.responseType === criteria.responseType
      );
    }

    if (!isDuplicate) {
      this.validationCriterias = [...this.validationCriterias, criteria];
    } else {
      this.toastr.error(ProjectConstants.VALIDATION_CRITERIA_DUPLICATE_SELECTION);
    }
  }

  editValidationCriteria(criteria: ProjectValidationCriteria): void {
    this.selectedAction = 'EDIT';

    // Store the exact index of the item being edited
    const index = this.validationCriterias.findIndex(c => c === criteria);
    this.selectedProjectValidationCriteriaForEdit = { ...criteria };
    this.editingCriteriaIndex = index;

    this.selectedCriteriaType = (criteria.responseType === 'Dropdown' || criteria.responseType === 'MultiSelect') ? CriteriaTypeOptions.Standard : CriteriaTypeOptions.Custom;
    this.selectedResponseType = criteria.responseType;

    if (this.selectedCriteriaType === CriteriaTypeOptions.Standard) {
      this.customCriteriaText = '';
      this.selectedCriteria = this.criteriaList.find(c => c.criteriaID === criteria.criteriaID) || null;
      this.isPartialCriteriaText = !!this.selectedCriteria?.isPartial;

      if (this.isPartialCriteriaText) {
        const template = this.selectedCriteria?.criteriaText || '';
        const userEntered = criteria.criteriaText || '';

        this.splitPartialCriteriaText = this.getSplitPartialCriteriaText(template);
        this.partialInputValue = this.extractPartialInputValues(template, userEntered);
      }


      if (this.selectedCriteria?.responses.length === 0) {
        this.customResponses = criteria.responsesText?.split(', ') || [];
      }
    } else {
      this.customCriteriaText = criteria.criteriaText!;
      this.responseDropdownValues = Object.values(ResponseTypeOptions);
      this.customResponses = [];
      this.partialCriteriaText = '';
      this.selectedCriteria = null;
    }
  }

  validateCriteria(): boolean {
    if (!this.selectedCriteriaType) {
      this.toastr.error(ProjectConstants.VALIDATION_REQUIRED_CRITERIATYPE);
      return false;
    }

    if (this.selectedCriteriaType === CriteriaTypeOptions.Standard) {
      if (this.selectedCriteria === null) {
        this.toastr.error(ProjectConstants.VALIDATION_REQUIRED_CRITERIA);
        return false;
      }

      if (this.isPartialCriteriaText) {
        const hasEmpty = this.partialInputValue.some(value => !value || value.trim() === '');

        if (hasEmpty) {
          this.toastr.error(ProjectConstants.VALIDATION_REQUIRED_CRITERIATEXT);
          return false;
        }
      }

      if (this.selectedCriteria?.responses.length === 0 && !this.customResponses.some(res => res && res.trim() !== '')) {
        this.toastr.error(ProjectConstants.VALIDATION_REQUIRED_RESPONSE);
        return false;
      }
    }

    if (this.selectedCriteriaType === 'Custom') {
      if (!this.customCriteriaText.trim()) {
        this.toastr.error(ProjectConstants.VALIDATION_REQUIRED_CRITERIATEXT);
        return false;
      }

      if (!this.selectedResponseType) {
        this.toastr.error(ProjectConstants.VALIDATION_REQUIRED_RESPONSETYPE);
        return false;
      }
    }

    if (this.selectedAction === 'EDIT' && this.selectedProjectValidationCriteriaForEdit) {
      const original = this.selectedProjectValidationCriteriaForEdit;

      // Case 1: Standard with fixed responses (non-partial)
      // Case 2: Custom criteria with same text and response type 
      if (
        (this.selectedCriteriaType === CriteriaTypeOptions.Standard &&
          this.selectedCriteria?.responses.length! > 0 &&
          !this.selectedCriteria?.isPartial &&
          this.selectedCriteria?.criteriaID === original.criteriaID) ||

        (this.selectedCriteriaType === CriteriaTypeOptions.Custom &&
          this.customCriteriaText.trim() === (original.criteriaText || '').trim() &&
          this.selectedResponseType === original.responseType)
      ) {
        //this.toastr.warning(ProjectConstants.VALIDATION_EDIT_NOCHANGES);
        this.resetCriteria();
        return false;
      }

      // Case 3: Standard with fixed responses (partial)
      else if (
        this.selectedCriteriaType === CriteriaTypeOptions.Standard &&
        this.selectedCriteria?.responses.length! > 0 &&
        this.selectedCriteria?.isPartial &&
        this.selectedCriteria?.criteriaID === original.criteriaID
      ) {
        const reconstructed = this.getReconstructedPartialCriteriaText().trim();
        const originalText = (original.criteriaText || '').trim();

        if (reconstructed === originalText) {
          //this.toastr.warning(ProjectConstants.VALIDATION_EDIT_NOCHANGES);
          this.resetCriteria();
          return false;
        }
      }

      // Case 4: Standard with partial text + optional custom responses 
      else if (
        this.selectedCriteriaType === CriteriaTypeOptions.Standard &&
        this.selectedCriteria?.isPartial
      ) {
        const reconstructed = this.getReconstructedPartialCriteriaText().trim();
        const originalText = (original.criteriaText || '').trim();

        const hasTextChanged = reconstructed !== originalText;

        const newResponses = this.customResponses.map(r => r.trim()).filter(r => !!r).sort().join(',');
        const oldResponses = (original.responsesText || '').split(',').map(r => r.trim()).filter(r => !!r).sort().join(',');

        const hasResponsesChanged = newResponses !== oldResponses;

        if (!hasTextChanged && !hasResponsesChanged) {
          //this.toastr.warning(ProjectConstants.VALIDATION_EDIT_NOCHANGES);
          this.resetCriteria();
          return false;
        }
      }

      // Case 5: Standard (non-partial) with optional custom responses
      else if (
        this.selectedCriteriaType === CriteriaTypeOptions.Standard &&
        !this.selectedCriteria?.isPartial &&
        this.customResponses.length &&
        original.responsesText
      ) {
        const newResponses = this.customResponses.map(r => r.trim()).filter(r => !!r).sort().join(',');
        const oldResponses = original.responsesText.split(',').map(r => r.trim()).filter(r => !!r).sort().join(',');

        if (newResponses === oldResponses) {
          //this.toastr.warning(ProjectConstants.VALIDATION_EDIT_NOCHANGES); 
          this.resetCriteria();
          return false;
        }
      }
    }


    return true;
  }

  getCommaSeparatedResponses(): string {
    if (this.selectedCriteriaType === 'Standard') {
      const isCustom = (this.selectedCriteria?.responses?.length ?? 0) === 0;

      // If custom responses, return as is
      if (isCustom) {
        return this.customResponses?.map(r => r.trim()).filter(r => !!r).join(', ') ?? '';
      }

      // For standard responses with partial text
      if (this.isPartialCriteriaText && this.partialInputValue.length) {
        return this.selectedCriteria?.responses?.map(r => {
          let responseValue = r.responseValue;

          this.splitPartialCriteriaText.forEach((part, idx) => {
            if (part.isInput && part.placeholder) {
              const placeholder = `[${part.placeholder}]`;
              const inputValue = this.partialInputValue[this.getInputIndex(idx)] || '';
              responseValue = responseValue.replace(placeholder, inputValue);
            }
          });

          return responseValue;
        }).join(', ') ?? '';
      }

      // Standard (non-partial) responses
      return this.selectedCriteria?.responses?.map(r => r.responseValue).join(', ') ?? '';
    }

    return '';
  }


  getResponseText(lstProjectValidationCriteriaResponse: any[]): string {
    if (!lstProjectValidationCriteriaResponse || lstProjectValidationCriteriaResponse.length === 0) {
      return '';
    }

    return lstProjectValidationCriteriaResponse
      .map(response => response.responseValue)
      .filter(value => !!value)
      .join(', ');
  }

  resetCriteria(): void {
    this.selectedCriteriaType = null;
    this.selectedCriteria = null;
    this.selectedResponseType = null;
    this.customCriteriaText = '';
    this.customResponses = [''];
    this.isPartialCriteriaText = false;
    this.partialCriteriaText = '';
    this.selectedAction = 'ADD';
  }

  removeValidationCriteria(index: number): void {
    if (!this.disableAgencySection && !this.disableAfterReview && this.validationCriterias.length > 1) {
      this.validationCriterias.splice(index, 1);
    }
  }

  emitUpdatedRows(): void {
    const changedItems = this.validationCriterias.filter(c => c.isChanged);
    this.updateValidationCriteriaRows.emit(changedItems);
  }

  trackByIndex(index: number, item: any): number {
    return index;
  }

  deleteCriteria(index: number, row: ProjectValidationCriteria): void {
    if (row.projectValidationCriteriaID) {
      this.confirmationService.confirm({
        message: ProjectConstants.VALIDATION_CRITERIA_DELETE_CONFIRM,
        accept: () => this.removeProjectCriteria(row, index)
      });
    } else {
      this.validationCriterias.splice(index, 1);
      this.resetCriteria();
    }
  }

  removeProjectCriteria(criteria: ProjectValidationCriteria, index: number): void {
    this.verificaionService.deleteprojectcriteria(criteria.projectValidationCriteriaID).subscribe(() => {
      this.validationCriterias.splice(index, 1);
      this.toastr.success(ProjectConstants.VALIDATION_DELETE_CRITERIA_SUCCESS);
      this.resetCriteria();
    });
  }

  private getSelectedCriteriaText(): string {
    if (this.selectedCriteriaType === 'Custom') {
      return this.customCriteriaText.trim();
    }
    return this.isPartialCriteriaText ? this.partialCriteriaText.trim() : this.selectedCriteria?.criteriaText?.trim() ?? '';
  }

  private buildProjectValidationCriteriaResponses(): ProjectValidationCriteriaResponse[] {
    const responses: ProjectValidationCriteriaResponse[] = [];

    if (this.isPartialCriteriaText && this.partialInputValue.length) {
      this.selectedCriteria?.responses?.forEach(el => {
        let responseValue = el.responseValue;

        // Replace all [PLACEHOLDER] with corresponding partialInputValue based on placeholder in splitPartialCriteriaText      
        this.splitPartialCriteriaText.forEach((part, idx) => {
          if (part.isInput && part.placeholder) {
            const placeholderToken = `[${part.placeholder}]`;
            const replacementValue = this.partialInputValue[this.getInputIndex(idx)] || '';
            responseValue = responseValue.replace(placeholderToken, replacementValue);
          }
        });

        responses.push({
          projectValidationCriteriaResponseID: 0,
          projectValidationCriteriaID: 0,
          criteriaResponseID: el.criteriaResponseID,
          responseValue: responseValue
        });
      });
    } else {
      // Normal case (no partial)
      this.selectedCriteria?.responses?.forEach(el => {
        responses.push({
          projectValidationCriteriaResponseID: 0,
          projectValidationCriteriaID: 0,
          criteriaResponseID: el.criteriaResponseID,
          responseValue: el.responseValue
        });
      });
    }


    const uniqueResponses = new Set<string>();

    this.customResponses.forEach(res => {
      const trimmed = res?.trim();
      if (trimmed && !uniqueResponses.has(trimmed)) {
        uniqueResponses.add(trimmed);
        responses.push({
          projectValidationCriteriaResponseID: 0,
          projectValidationCriteriaID: 0,
          criteriaResponseID: 0,
          responseValue: trimmed
        });
      }
    });

    return responses;
  }

  buildSplitPartialText(text: string): void {
    const regex = /\[([^\]]+)\]/g;
    let result: any[] = [];
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(text)) !== null) {
      const [fullMatch, placeholderText] = match;

      // Add static text before the input
      if (match.index > lastIndex) {
        result.push({
          text: text.substring(lastIndex, match.index),
          isInput: false
        });
      }

      // Add input placeholder
      result.push({
        text: '',
        isInput: true,
        placeholder: placeholderText
      });

      lastIndex = match.index + fullMatch.length;
    }

    // Add any remaining text after the last match
    if (lastIndex < text.length) {
      result.push({
        text: text.substring(lastIndex),
        isInput: false
      });
    }

    this.splitPartialCriteriaText = result;
    this.partialInputValue = new Array(result.filter(r => r.isInput).length).fill('');
  }

  getReconstructedPartialCriteriaText(): string {
    let inputIndex = 0;

    return this.splitPartialCriteriaText.map(part => {
      if (part.isInput) {
        const input = this.partialInputValue?.[inputIndex]?.trim() || '';
        inputIndex++;
        return input;
      } else {
        return part.text;
      }
    }).join('');
  }

  getInputIndex(viewIndex: number): number {
    return this.splitPartialCriteriaText
      .slice(0, viewIndex)
      .filter(p => p.isInput)
      .length;
  }

  getSplitPartialCriteriaText(template: string): { text: string; isInput: boolean; placeholder: string }[] {
    const regex = /\[([^\]]*)\]/g;
    const parts: { text: string; isInput: boolean; placeholder: string }[] = [];

    let lastIndex = 0;
    let match;

    while ((match = regex.exec(template)) !== null) {
      if (match.index > lastIndex) {
        parts.push({ text: template.substring(lastIndex, match.index), isInput: false, placeholder: '' });
      }
      parts.push({ text: '', isInput: true, placeholder: match[1] || '' });
      lastIndex = regex.lastIndex;
    }

    if (lastIndex < template.length) {
      parts.push({ text: template.substring(lastIndex), isInput: false, placeholder: '' });
    }

    return parts;
  }

  extractPartialInputValues(template: string, filledText: string): string[] {
    const inputRegex = /\[([^\]]+)\]/g;

    // Escape special regex characters in the static parts of the template
    const escapeRegex = (str: string) => str.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');

    // Build the pattern by replacing each [placeholder] with a capture group  
    let pattern = '';
    let lastIndex = 0;
    let match;

    while ((match = inputRegex.exec(template)) !== null) {
      const staticText = template.slice(lastIndex, match.index);
      pattern += escapeRegex(staticText) + '(.+?)';
      lastIndex = match.index + match[0].length;
    }

    // Add the final static text (after the last placeholder)  
    const finalStatic = template.slice(lastIndex);
    pattern += escapeRegex(finalStatic);

    const regex = new RegExp('^' + pattern + '$');
    const result = filledText.match(regex);

    if (result) {
      // Remove the first element which is the full match   
      return result.slice(1).map(v => v.trim());
    }

    return []; // fallback if pattern didn't match
  }

}